import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  collection,
  getDocs,
  query,
  where,
  documentId,
  Timestamp,
} from "firebase/firestore";
import { RootState } from "../store/store";
import { db } from "../firebase/FirebaseCredential";

interface Branch {
  id: string;
  name?: string;
  company_id: string;
  branch_name: string;
  [key: string]: any;
}

interface BranchState {
  branchesList: Branch[];
  loading: boolean;
  error: string | null;
}

const initialState: BranchState = {
  branchesList: [],
  loading: false,
  error: null,
};

// 🔥 Thunk to fetch branches dynamically
export const fetchBranches = createAsyncThunk(
  "branch/fetchBranches",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const companyId = state.session.company_id;
      const roleType = state.session.role_type;
      const branchIds = state.session.mapped_branch_id || [];

      if (!companyId) throw new Error("Missing company_id in session");
      if (!roleType) throw new Error("Missing role_type in session");
      let branchQuery;

      if (roleType === "company_admin") {
        // Fetch branches by company_id
        branchQuery = query(
          collection(db, "branch"),
          where("company_id", "==", companyId),
          where("status", "==", "Active")
        );
      } else {
        // Fetch branches by mapped branch IDs
        if (branchIds.length === 0) return []; // No branches found
        branchQuery = query(
          collection(db, "branch"),
          where(documentId(), "in", branchIds)
        );
      }

      const branchCollection = await getDocs(branchQuery);
      const branches:any = branchCollection.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          created_at:
            data.created_at instanceof Timestamp
              ? data.created_at.toDate().toISOString()
              : null,
        };
      });

      // ✅ Add "All Branches" for company_admin
      if (roleType === "company_admin") {
        branches.unshift({ id: "all_branches", name: "All Branches" });
      }

      return branches;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  }
);

// 🔥 Redux slice with extra reducers for thunk actions
const branchSlice = createSlice({
  name: "branch",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBranches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBranches.fulfilled, (state, action) => {
        state.branchesList = action.payload as any[];
        state.loading = false;
      })
      .addCase(fetchBranches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default branchSlice.reducer;

// import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// interface Branch {
//   id: string;
//   company_id: string;
//   branch_name: string;
//   [key: string]: any; // Allows additional fields
// }

// interface BranchState {
//   branches: Branch[];
//   loading: boolean;
//   error: string | null;
// }

// const initialState: BranchState = {
//   branches: [],
//   loading: false,
//   error: null,
// };

// const branchSlice = createSlice({
//   name: "branch",
//   initialState,
//   reducers: {
//     fetchBranchesStart: (state) => {
//       state.loading = true;
//       state.error = null;
//     },
//     fetchBranchesSuccess: (state, action: PayloadAction<Branch[]>) => {
//       state.branches = action.payload;
//       state.loading = false;
//     },
//     fetchBranchesFailure: (state, action: PayloadAction<string>) => {
//       state.loading = false;
//       state.error = action.payload;
//     },
//   },
// });

// export const { fetchBranchesStart, fetchBranchesSuccess, fetchBranchesFailure } =
//   branchSlice.actions;
// export default branchSlice.reducer;
